import { sb } from '../supabase';

// Growth OS «رحلة العميل» — the sign-up funnel and the service funnels, from
// growth_events_clean only. Steps count distinct browsers (anon_id): a person
// who retries a code is one person who reached that step, not three.

export const AUTH_EVENTS = ['auth_viewed', 'auth_method_chosen', 'otp_requested', 'otp_verified', 'otp_failed', 'signup_completed', 'login_completed'];
export const SERVICE_EVENTS = ['service_form_started', 'service_fee_shown', 'service_submitted', 'service_fee_paid', 'service_form_abandoned', 'service_form_field_error'];
export const ROW_CAP = 20000;

const CODE_METHODS = ['phone', 'email', 'whatsapp_code'];
export const METHOD_AR = { phone: 'رقم الجوال (رمز واتساب)', email: 'البريد (رمز)', google: 'Google', apple: 'Apple', phone_password: 'جوال وكلمة مرور', email_password: 'بريد وكلمة مرور', whatsapp_code: 'رمز واتساب' };
export const CHANNEL_AR = { whatsapp: 'واتساب', email: 'البريد', sms: 'رسالة نصية' };
export const SERVICE_AR = { rfq: 'اطلب عرض سعر', managed: 'الطلب المُدار', idea: 'صنّع فكرتك', factory: 'طلب من مصنع' };
export const FIELD_AR = {
  title: 'اسم المنتج', description: 'الوصف', quantity: 'الكمية', unit: 'الوحدة', payment_plan: 'خطة الدفع',
  sample_requirement: 'العينة', ship_city: 'مدينة الشحن', delivery_preference: 'التوصيل', phone: 'رقم الجوال',
  phone_server: 'رقم غير موثّق', draft: 'مسودة الفكرة', idea_mode: 'طريقة الطلب', phone_verify: 'توثيق الرقم',
};
export const REASON_AR = {
  otp_expired: 'انتهت صلاحية الرمز', invalid_credentials: 'رمز غير صحيح', no_session: 'لم يكتمل التحقق',
  no_account: 'لا يوجد حساب بهذا الرقم', over_sms_send_rate_limit: 'تجاوز حد الإرسال', over_email_send_rate_limit: 'تجاوز حد الإرسال',
  over_request_rate_limit: 'تجاوز حد المحاولات', otp_disabled: 'التسجيل بالرمز غير مسموح', validation_failed: 'بيانات غير صالحة',
  sms_send_failed: 'تعذّر إرسال الرمز', http_429: 'تجاوز حد المحاولات', http_500: 'خطأ في الخادم',
};

const distinct = (rows) => new Set(rows.map((e) => e.anon_id)).size;

export function summarizeJourney(events) {
  const by = (name, pred = () => true) => events.filter((e) => e.event_name === name && pred(e.props || {}));
  const signup = (p) => p.mode === 'signup';

  const chosen = by('auth_method_chosen', signup);
  const steps = [
    { key: 'opened', label: 'فتح التسجيل', n: distinct(by('auth_viewed', signup)) },
    { key: 'chose', label: 'اختار طريقة', n: distinct(chosen) },
    { key: 'sent', label: 'أُرسل الرمز', n: distinct(by('otp_requested', signup)), codeOnly: true },
    { key: 'verified', label: 'تحقّق الرمز', n: distinct(by('otp_verified', signup)), codeOnly: true },
    { key: 'created', label: 'أُنشئ الحساب', n: distinct(by('signup_completed')) },
  ];
  // Each step's rate is measured against the step its people actually came
  // from: «code sent» against those who chose a CODE method (Google has no
  // code), «account created» against everyone who chose any method. A rate
  // against the row above would read 128% once Google sign-ups skip the code.
  const choseCode = distinct(chosen.filter((e) => CODE_METHODS.includes(e.props.method)));
  steps[1].prev = steps[0].n;
  steps[2].prev = choseCode; steps[2].prevLabel = 'ممن اختار طريقة برمز';
  steps[3].prev = steps[2].n;
  steps[4].prev = steps[1].n; steps[4].prevLabel = 'ممن اختار طريقة';
  steps[0].prev = null;

  // Who stopped at each step: reached it and NO later step (Google skips the
  // code steps, so «the next row» is not the test). Their latest session opens in Clarity.
  const anonLast = {};
  events.forEach((e) => {
    if (!e.session_id) return;
    const at = Date.parse(e.occurred_at || 0);
    if (!anonLast[e.anon_id] || anonLast[e.anon_id].at < at) anonLast[e.anon_id] = { session_id: e.session_id, at };
  });
  const stepSets = [by('auth_viewed', signup), chosen, by('otp_requested', signup), by('otp_verified', signup), by('signup_completed')]
    .map((rows) => new Set(rows.map((e) => e.anon_id)));
  steps.forEach((st, i) => {
    const later = new Set(stepSets.slice(i + 1).flatMap((x) => [...x]));
    st.drops = i === steps.length - 1 ? [] : [...stepSets[i]].filter((a) => !later.has(a)).map((a) => anonLast[a]).filter(Boolean)
      .sort((x, y) => y.at - x.at).slice(0, 8);
  });

  // per method: how many chose it and how many accounts it made
  const methods = {};
  chosen.forEach((e) => { const m = e.props.method; (methods[m] = methods[m] || { method: m, chose: new Set(), created: new Set() }).chose.add(e.anon_id); });
  by('signup_completed').forEach((e) => { const m = e.props.method; (methods[m] = methods[m] || { method: m, chose: new Set(), created: new Set() }).created.add(e.anon_id); });
  const methodRows = Object.values(methods).map((m) => ({ method: m.method, chose: m.chose.size, created: m.created.size, code: CODE_METHODS.includes(m.method) }))
    .sort((a, b) => b.chose - a.chose);

  // code failures, per channel and mode (sign-up, sign-in, checkout)
  const fails = {};
  by('otp_failed').forEach((e) => {
    const k = `${e.props.channel || '?'}|${e.props.mode || '?'}`;
    const f = fails[k] || (fails[k] = { channel: e.props.channel || '?', mode: e.props.mode || '?', total: 0, reasons: {} });
    f.total += 1;
    const r = e.props.reason || 'unknown';
    f.reasons[r] = (f.reasons[r] || 0) + 1;
  });
  const failures = Object.values(fails).map((f) => ({ ...f, top: Object.entries(f.reasons).sort((a, b) => b[1] - a[1]).slice(0, 3) }))
    .sort((a, b) => b.total - a.total);
  const sent = {};
  by('otp_requested').forEach((e) => { const k = `${e.props.channel}|${e.props.mode}`; sent[k] = (sent[k] || 0) + 1; });
  failures.forEach((f) => { f.sent = sent[`${f.channel}|${f.mode}`] || 0; });

  // services: started → fee shown → submitted → fee paid, per service
  const services = ['rfq', 'managed', 'idea'].map((svc) => {
    const of = (name) => by(name, (p) => p.service === svc);
    const errors = {};
    of('service_form_field_error').forEach((e) => { errors[e.props.field] = (errors[e.props.field] || 0) + 1; });
    const feeService = svc !== 'rfq';
    const st = [
      { key: 'started', label: 'بدأ النموذج', n: distinct(of('service_form_started')) },
      ...(feeService ? [{ key: 'fee', label: 'رأى الرسوم', n: distinct(of('service_fee_shown')) }] : []),
      { key: 'submitted', label: 'أرسل الطلب', n: distinct(of('service_submitted')) },
      ...(feeService ? [{ key: 'paid', label: 'دفع الرسوم', n: of('service_fee_paid').length }] : []),
    ];
    st.forEach((s, i) => { s.prev = i ? st[i - 1].n : null; });
    return { service: svc, steps: st, abandoned: distinct(of('service_form_abandoned')),
      errors: Object.entries(errors).sort((a, b) => b[1] - a[1]).slice(0, 3) };
  });

  return { steps, methods: methodRows, failures, services };
}

export async function fetchJourney(win) {
  const { data, error } = await sb.from('growth_events_clean').select('event_name, anon_id, session_id, occurred_at, props')
    .in('event_name', [...AUTH_EVENTS, ...SERVICE_EVENTS])
    .gte('occurred_at', new Date(win.start).toISOString()).lt('occurred_at', new Date(win.end).toISOString())
    .limit(ROW_CAP);
  if (error) throw error;
  const rows = data || [];
  return { ...summarizeJourney(rows), capped: rows.length >= ROW_CAP };
}
